import { useCallback, useEffect, useState } from 'react';
import { FaChartBar, FaCalendarAlt, FaUsers, FaInfoCircle } from 'react-icons/fa';
import { getPrograms } from '../../../services/ProgramServices';
import CardHeader from '../../../components/Card/CardHeader';
import AttendanceChart from './AttendanceChart';
import { getProgramWiseAttendanceCount, getAttendanceSummary, getAttendanceMarkedTimes } from '../../../services/AppManagement/AttendanceService';
import { getActivatedInActivatedParentAppUserCount } from '../../../services/StudentService';
import SummaryStatCard from '../../dashboard/SummaryStatCard';
import Input from '../../../components/Input/Input';
import Loader from '../../../components/Loader/Loader';
import AttendanceSummary from './AttendanceSummary';
import AttendanceMarkedTimes from './AttendanceMarkedTimes';
import Tooltip from '../../../components/Tooltip/Tooltip';
import '../../dashboard/Dashboard.css';
import './AttendanceDashboard.css';

const AttendanceDashboard = () => {
  const session_id = localStorage.getItem('session_id');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [parentUsers, setParentAppUsers] = useState({ activated: 0, inactivated: 0 });
  const [isLoader, setIsLoader] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [data, setData] = useState([]);
  const [attendanceMarkedUnMarked, setAttendanceMarkedUnMarked] = useState([]);

  const getData = useCallback(async (program_ids, date) => {
    try {
      setIsLoader(true);
      const response = await getProgramWiseAttendanceCount(program_ids, session_id, date);
      if (response.status === 200) {
        setData(response.data.data);
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setIsLoader(false);
    }
  }, [session_id]);

  const fetchPrograms = useCallback(async () => {
    const response = await getPrograms();
    const program_ids = response.data && response.data.map((program) => program.id);
    getData(program_ids, date);
  }, [date, getData]);

  const fetchParentAppUsers = useCallback(async () => {
    try {
      const response = await getActivatedInActivatedParentAppUserCount(session_id);
      if (response.status === 200) {
        setParentAppUsers(response.data.data);
      }
    }
    catch (error) {
      console.error(error)
    }
  }, [session_id]);

  const fetchAttendanceMarkedTimes = useCallback(async (date) => {
    try {
      setIsLoader(true);
      const response = await getAttendanceMarkedTimes(date);
      if (response.status === 200) {
        setAttendanceMarkedUnMarked(response.data.data.data);
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setIsLoader(false);
    }
  }, []);

  const fetchAttendanceSummary = useCallback(async (date) => {
    try {
      const response = await getAttendanceSummary(date);
      if (response.status === 200) {
        setAttendanceSummary(response.data.data);
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setIsLoader(false);
    }
  }, []);

  const dateChangeHandler = (e) => {
    setIsLoader(true);
    setDate(e.target.value);
  }

  useEffect(() => {
    fetchPrograms();
    fetchParentAppUsers();
    fetchAttendanceSummary(date);
    fetchAttendanceMarkedTimes(date);
  }, [date, fetchAttendanceMarkedTimes, fetchAttendanceSummary, fetchParentAppUsers, fetchPrograms]);

  return (
    <div className='card'>
      <CardHeader
        title='Attendance Dashboard'
        icon={<FaChartBar />}
        button={''}
      />
      <div className='card-body'>
        <div className="row">
          <SummaryStatCard
            title="Parent App Users"
            variant="teal"
            icon="fa-solid fa-user-graduate"
            count={parentUsers.activated} />
          <SummaryStatCard
            title="Parent App Not Using"
            variant="indigo"
            icon="fa-solid fa-user-tie"
            count={parentUsers.inactivated} />
          <SummaryStatCard
            title="Total Student"
            variant="amber"
            icon="fa-solid fa-users"
            count={parentUsers.inactivated + parentUsers.activated} />
        </div>

        <div className="dashboard-toolbar">
          <Input
            type="date"
            name="date"
            label="Select Date"
            id='date'
            onChangeHandler={dateChangeHandler}
            value={date} />
        </div>

        {isLoader ? <Loader /> :
          <>
            <div className="dashboard-section">
              <div className="dashboard-section-title">
                <FaCalendarAlt /> <span>Attendance Mark Times — {date}</span>
              </div>
              <AttendanceMarkedTimes data={attendanceMarkedUnMarked} />
            </div>

            <div className="dashboard-section">
              <div className="dashboard-section-title">
                <FaUsers /> <span>Gender Wise Attendance Summary</span>
              </div>
              <AttendanceSummary summary={attendanceSummary} />
            </div>

            <div className="dashboard-section">
              <div className="dashboard-section-title">
                <FaChartBar /> <span>Class Wise Attendance Summary</span>
                <Tooltip tooltipText="Class and present, absent and leave student count for the selected date">
                  <FaInfoCircle style={{ cursor: 'pointer' }} />
                </Tooltip>
              </div>
              <AttendanceChart data={data} />
            </div>
          </>
        }
      </div>
    </div>
  );
};

export default AttendanceDashboard;
